import type * as ents from '@attain-apps/entities'
import * as store from '@attain-apps/fe/web/main/lib/store'

import type { DocumentKey } from './models'
import { State } from './state'

type UploadInput = ents.core.agent.KnowledgeBase.Management.UploadInput
type FileTarget = ents.core.agent.KnowledgeBase.Management.FileTarget

/** Defines upload, download, and delete operations for attribute documents. */
export function getActionsConfig() {
  return {
    clearSelection: store.actions.standalone({
      data: store.actions.Data.asIs<{ key: DocumentKey }>(),
      reducer: ({ state, data: { key } }): State =>
        State.removeSelection(state, key),
    }),
    delete: store.actions.request({
      input: store.actions.Data.asIs<{
        key: DocumentKey
        target: FileTarget
      }>(),
      output: store.actions.Data.asIs<boolean>(),
      reducers: {
        cancellation: ({ state, data: { request } }): State =>
          State.removeDeletionEntry(state, request.key),
        error: ({ state, data: { error, request } }): State =>
          State.setDeletionEntry(state, request.key, { error }),
        request: ({ state, data }): State =>
          State.setDeletionEntry(state, data.key, { loading: true }),
        success: ({ state, data: { request, response } }): State =>
          State.setDeletionEntry(state, request.key, { data: response }),
      },
    }),
    download: store.actions.request({
      input: store.actions.Data.asIs<{
        key: DocumentKey
        target: FileTarget
      }>(),
      output: store.actions.Data.asIs<string>(),
      reducers: {
        cancellation: ({ state, data: { request } }): State =>
          State.removeDownloadEntry(state, request.key),
        error: ({ state, data: { error, request } }): State =>
          State.setDownloadEntry(state, request.key, { error }),
        request: ({ state, data }): State =>
          State.setDownloadEntry(state, data.key, { loading: true }),
        success: ({ state, data: { request, response } }): State =>
          State.setDownloadEntry(state, request.key, { data: response }),
      },
    }),
    select: store.actions.standalone({
      data: store.actions.Data.asIs<{
        files: readonly File[]
        key: DocumentKey
      }>(),
      reducer: ({ state, data: { key, files } }): State =>
        State.setSelection(state, key, files),
    }),
    upload: store.actions.request({
      input: store.actions.Data.asIs<{
        file: File
        input: UploadInput
        key: DocumentKey
      }>(),
      output: store.actions.Data.asIs<void>(),
      reducers: {
        cancellation: ({ state, data: { request } }): State =>
          State.removeUploadEntry(state, request.key),
        error: ({ state, data: { error, request } }): State =>
          State.setUploadEntry(state, request.key, { error }),
        request: ({ state, data }): State =>
          State.setUploadEntry(state, data.key, { loading: true }),
        success: ({ state, data: { request } }): State =>
          State.removeUploadEntry(state, request.key),
      },
    }),
  } as const satisfies store.actions.Config.Constraint<State>
}
